'use client';
import { useState, useRef, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import MobileBottomNav from './MobileBottomNav';
import ViewOnlyBanner from './ViewOnlyBanner';
import LiveScoreDock from '@/components/sports/LiveScoreDock';
import { isMarketingPublicPath } from '@/constants/publicRoutes';

// Public sub-routes exposed by the iframed school theme. Must stay in
// sync with `THEME_PUBLIC_PATHS` in `middleware.ts` — any path listed
// there needs to also skip the authenticated admin chrome here, or
// logged-in admins Ctrl+clicking a theme nav link end up seeing the
// admin sidebar wrapped around their own public landing page.
const THEME_PUBLIC_PATHS = new Set([
  '/academics',
  '/admission-process',
  '/apply',
  '/blog',
  '/blogs',
  '/contact',
  '/enquiry',
  '/gallery',
  '/notices',
  '/privacy',
  '/results',
  '/rules',
  '/syllabus',
  '/terms',
]);

const isThemePublicPath = (pathname) => {
  if (!pathname) return false;
  if (THEME_PUBLIC_PATHS.has(pathname)) return true;
  // Support trailing slashes and shallow sub-paths (e.g. /blogs/my-post).
  for (const p of THEME_PUBLIC_PATHS) {
    if (pathname.startsWith(`${p}/`)) return true;
  }
  return false;
};

export default function ConditionalLayout({ children }) {
  const pathname = usePathname();
  const isPublicRoute =
    isMarketingPublicPath(pathname) ||
    pathname?.startsWith('/auth') ||
    pathname?.startsWith('/onboarding/pay') ||
    pathname === '/domain-not-found' ||
    pathname === '/expired' ||
    pathname === '/renew' ||
    isThemePublicPath(pathname);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [hasBottomNav, setHasBottomNav] = useState(false);
  const mainContentRef = useRef(null);

  // Check if user is logged in (has bottom nav) and if bottom nav is not hidden
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const updateBottomNavState = () => {
        const role = sessionStorage.getItem('userRole');
        const isHidden = document.body.classList.contains('hide-mobile-bottom-nav');
        setHasBottomNav(!!role && !isHidden);
      };

      // Initial check
      updateBottomNavState();

      // Watch for class changes on body element
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            updateBottomNavState();
          }
        });
      });

      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      });

      return () => observer.disconnect();
    }
  }, [pathname]);

  // Reset scroll position when pathname changes
  useEffect(() => {
    // Skip scroll reset if navigating to a hash anchor (e.g. /#features)
    if (window.location.hash) return;

    const scrollToTop = () => {
      if (mainContentRef.current) {
        mainContentRef.current.scrollTop = 0;
      }
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
      }
    };

    // Attempt immediate reset
    scrollToTop();

    // Attempt reset after short delay to handle router transitions/animations
    const rafId = requestAnimationFrame(() => {
      scrollToTop();
    });

    const timeoutId = setTimeout(() => {
      scrollToTop();
    }, 100);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [pathname]);

  if (isPublicRoute) {
    return <>{children}</>;
  }

  return (
    <div className="h-screen overflow-hidden bg-background-main flex">
      {/* Desktop Sidebar - hidden on mobile */}
      <Sidebar
        isMobileSidebarOpen={isMobileSidebarOpen}
        onMobileSidebarClose={() => setIsMobileSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col main-content min-w-0">
        <div className="app-header">
          <Header
            isMobileSidebarOpen={isMobileSidebarOpen}
            onMobileMenuClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
          />
        </div>
        <main
          key={pathname}
          ref={mainContentRef}
          className={`flex-1 ${hasBottomNav ? 'pb-28' : 'pb-0'} md:pb-4 xl:pb-0 overflow-y-auto mt-[60px] xl:mt-0`}
        >
          <ViewOnlyBanner />
          {children}
        </main>
        <Footer />
      </div>

      {/* The school's scoreboard, on every authenticated page and for every
          role. Renders nothing unless a match is actually being played. */}
      <LiveScoreDock />

      {/* Mobile Bottom Navigation - only visible on mobile */}
      <div className="app-mobile-nav">
        <MobileBottomNav />
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <>
          {/* Backdrop */}
          <div
            className="xl:hidden fixed inset-0 bg-black/50 z-[60]"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
          {/* Sidebar Overlay */}
          <div className="xl:hidden fixed inset-0 z-[70] pointer-events-none">
            <div className="pointer-events-auto w-full h-full">
              <Sidebar
                isMobileMode={true}
                isMobileSidebarOpen={isMobileSidebarOpen}
                onMobileSidebarClose={() => setIsMobileSidebarOpen(false)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

